/**
 * The values a `FieldSpec[]` form holds, and the object it sends.
 *
 * `schemaFields.ts` turns a JSON Schema into fields; this turns those fields into
 * the three value shapes a form moves between, and it is the half both forms in the
 * building need:
 *
 *   - **blank** — what a control starts as, so react-hook-form is never handed an
 *     uncontrolled box (`emptyValue`);
 *   - **filled in** — a stored row, in the shape the control holds it (`formValuesOf`);
 *   - **on the wire** — the box's text, back as the type the API takes
 *     (`wireValuesOf`), and what a box somebody *emptied* is sent as, which is not
 *     the same thing as a box nobody filled in (`clearedOf`).
 *
 * It was `ActionForm.tsx`'s, privately, until a second form arrived: the client's
 * widget configuration form (`.plans/ai-product-placer/plan/P5-v1-1.md` item (d2))
 * draws the same `FieldSpec`s over a widget's snapshotted `config_schema` and needs
 * the same three answers. A second copy is not a near miss — it is the pair of rules
 * that says a `<select>` over `{ type: 'number' }` sends `1` and not `"1"`, and a
 * form that had its own copy would send the string on one page and the number on the
 * other.
 *
 * **This is the whole of it, and none of it knows what an action is.** `ActionForm`
 * keeps the two conversions that are an *action's* and not a shape's — `tasks_assign`'s
 * one control answering two fields, and `moneyFieldValue` for a page pre-filling an
 * amount off a row it is editing — and delegates the rest here. The folder is what
 * enforces the split, exactly as it does for `schemaFields.ts`: `components/studio`
 * reaches into `components/portal` and this folder never reaches back.
 */

import { CURRENCIES, fromMajorUnits, zero, type Currency, type Money } from '@fareast/shared'

import type { FieldSpec, FieldWidget } from './schemaFields.js'

/** What one field holds while the form is being filled in. */
export type FieldValue =
  | string
  | boolean
  | readonly string[]
  | { readonly major: string; readonly currency: string }
  | readonly Record<string, unknown>[]
  | undefined

export type FormValues = Record<string, unknown>

/** What a `money` control holds: the major-unit box, and the currency beside it. */
export type MoneyValue = { readonly major: string; readonly currency: Currency }

export function isMoneyValue(value: unknown): value is MoneyValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'major' in value &&
    'currency' in value
  )
}

/* ------------------------------------------------------------------- blank */

/** What a field starts as, so react-hook-form is never handed an uncontrolled box. */
export function emptyValue(field: FieldSpec): FieldValue {
  switch (field.widget) {
    case 'checkbox':
      return false
    case 'money':
      return { major: '', currency: field.currency ?? CURRENCIES[0] }
    case 'multiselect':
      return []
    case 'rows':
      return []
    default:
      return ''
  }
}

/** The blank row a `rows` field adds when somebody presses Add. */
export function emptyRow(field: FieldSpec): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  for (const inner of field.fields ?? []) row[inner.name] = emptyValue(inner)
  return row
}

/* --------------------------------------------------------------- filled in */

/**
 * Every field, blank or as the caller filled it in.
 *
 * The caller is a studio page passing `defaults` to `ActionForm`, or the client's
 * configuration form passing the widget's stored `config` — which is the same kind
 * of thing: a row, in the API's own shapes, being put into boxes.
 */
export function formValuesOf(
  fields: readonly FieldSpec[],
  values: Readonly<Record<string, unknown>> | undefined,
): FormValues {
  const filled: FormValues = {}
  for (const field of fields) {
    const given = values?.[field.name]
    // `null` is a nullable field with no value — `due_at: task.due_at` on an undated
    // task — and a box holds it as blank; `null` in the form state fails `z.string()`.
    filled[field.name] =
      given === undefined || given === null ? emptyValue(field) : asFormValue(field, given)
  }
  return filled
}

/**
 * One stored value, in the shape the control holds it in.
 *
 * A box holds a **string**, whatever the schema says the field is: `Input
 * type="number"` reads and writes text, and a `<select>`'s value is a string even
 * over a numeric enum. So `defaults={{ market_tier: 1 }}` — the obvious thing for a
 * page to write, reading the number straight off the row it is editing — used to fail
 * the resolver with "expected string" on a field nobody had touched, which presents
 * as a form that will not submit and shows no error anywhere.
 *
 * **A `tags` list is the same mistake one type further along**, and it arrived with
 * the config form: `origins` is stored as `["https://casaturca.co", …]` and its
 * control is a textarea, one entry per line. An array handed to a textarea renders as
 * empty (`Form.tsx` `asText` has no text form for an object), so the studio's own
 * *Set the configuration* form would have shown a blank box over a live value and
 * saved the blank. `wireValueOf` splits the lines back apart on the way out; this is
 * the other direction, and the two are two halves of one rule.
 *
 * Only these four convert. A `money` default is `{ major, currency }`, a `multiselect`
 * default is an array and a `rows` default is an array of rows; a caller passing any
 * of those is passing the control's own shape and is left alone.
 */
export function asFormValue(field: FieldSpec, given: unknown): unknown {
  const isTextual = field.widget === 'number' || field.widget === 'qty' || field.widget === 'select'
  if (isTextual && typeof given === 'number') return String(given)
  if (field.widget === 'tags' && Array.isArray(given)) {
    return given.map((entry) => String(entry)).join('\n')
  }
  /*
   * An RFC 3339 instant into a `datetime-local` box. The box accepts only a zoneless
   * `YYYY-MM-DDTHH:mm`, and sanitises anything else — `…T17:00:00.000Z` — to an empty
   * string, so a due date read off the row showed as a blank box over a form state
   * that still held the old instant. The reader's zone, because `wireValueOf` reads
   * the box back in the same one.
   */
  if (field.widget === 'datetime' && typeof given === 'string') return localDateTimeOf(given)
  return given
}

/**
 * `2026-09-04T14:00:00.000Z` → `2026-09-04T17:00` in a UTC+3 browser. Unparseable passes.
 *
 * Exported since session 38: the Tasks list edits a due date in the cell rather than
 * in a dialog, and a second copy of this conversion is the bug `f3c8c6c` already had
 * to fix once — a box pre-filled with an instant it refuses, showing blank over a
 * value that was still there.
 */
export function localDateTimeOf(value: string): string {
  const instant = new Date(value)
  if (Number.isNaN(instant.getTime())) return value
  const two = (n: number): string => String(n).padStart(2, '0')
  return (
    `${String(instant.getFullYear())}-${two(instant.getMonth() + 1)}-${two(instant.getDate())}` +
    `T${two(instant.getHours())}:${two(instant.getMinutes())}`
  )
}

/**
 * `2026-09-04T17:00` → `2026-09-04T14:00:00.000Z`. Unparseable passes.
 *
 * A `datetime-local` box holds a wall time with no zone, and the reader's zone is the
 * one they mean — `new Date(local)` is what the platform reads it as, and
 * `toISOString` is the RFC 3339 the API takes. This is not a clock read: nothing here
 * asks what time it is now, which is why it is not `integrations/clock.ts`'s.
 */
export function instantOf(value: string): string {
  const instant = new Date(value)
  return Number.isNaN(instant.getTime()) ? value : instant.toISOString()
}

/* ------------------------------------------------------------- on the wire */

/**
 * The object the form sends, from the form's own state.
 *
 * A field nobody filled in is **left out** rather than sent empty: an action's input
 * is a `z.strictObject` and a config schema sets `additionalProperties: false`, so
 * every optional field means "leave it out" and an empty box would be a 400 about a
 * value the reader never typed.
 *
 * **A field somebody emptied is not that field**, and `opened` is how the two are told
 * apart: what the boxes held when the form was drawn — a page's `defaults`, as it
 * passed them. A box that started with a value and is blank now has had its value
 * *taken away*, and over a partial update leaving it out says the opposite. So it is
 * sent as whatever its schema takes for "nothing" (`clearedOf`), and a box that
 * started blank and is still blank is left out exactly as it always was.
 *
 * With no `opened` there is no such thing as an emptied field, and every form that
 * passes none sends byte for byte what it sent before: a form with no defaults, a
 * row inside a `rows` field, and the two forms over a widget's configuration, which
 * each say why at their own call.
 */
export function wireValuesOf(
  fields: readonly FieldSpec[],
  values: FormValues,
  opened?: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const field of fields) {
    const value = values[field.name]
    if (!isBlank(value)) {
      body[field.name] = wireValueOf(field, value)
      continue
    }
    if (!wasFilled(field, opened)) continue
    /*
     * Emptied, and the schema has no word for it: there is nothing that could be
     * sent. It is left out here because this function builds a body and cannot
     * refuse one — `cannotBeEmptied` is the same question asked before the form is
     * drawn, and what `ActionForm` makes such a field required by, so its resolver
     * has refused the blank under the box long before a body is built.
     */
    const cleared = clearedOf(field)
    if (cleared !== null) body[field.name] = cleared.value
  }
  return body
}

/* ------------------------------------------------ a change, and what from */

/**
 * What a form over a **stored row** sends: the fields the reader changed, and what
 * each of them held when the form was drawn.
 *
 * `wireValuesOf` sends every box that holds something, which is right for a form
 * that creates and wrong for one that edits a row other people edit too — twice
 * over. A box nobody touched was sent back holding what it held at mount, so a
 * window open on a task's amount wrote its old money reference over the one a
 * colleague had just saved. And a box the reader *did* change was written whatever
 * it held by then: two people in one task's notes, and the second Save replaced the
 * first with nothing refused and nothing said.
 *
 * So `changes` is only what differs from what the form opened holding — compared on
 * the wire, after the same conversions, so a date the box shows to the minute is not
 * "changed" for having been stored to the second — and `was` is the other half the
 * API asks for: what each changed field was changed **from**, which it refuses the
 * change over when the field holds something else now.
 *
 * **`decided`** are the fields somebody else has already been found to have changed,
 * which the reader has been told about under the box. What the box holds then is a
 * decision — "put mine in its place" — and is sent **whatever it is and on no
 * condition**. Whatever it is, because the reader who puts the box back to what it
 * opened with means "as it was", and comparing with the opening value called that
 * "nothing changed" and shut the window over the other person's. On no condition,
 * because the only thing this form could say it was made from is a value the page
 * has not read while the window is open; the reader has been told once, and the
 * second press is theirs to make.
 *
 * **And a decided box that holds nothing is sent as nothing** (`clearedOf`).
 * `wireValuesOf` leaves a blank box out when it also *opened* blank, which is
 * "untouched" for every other field and is not for this one: the reader typed a
 * reference into an empty box, was told a colleague had put one there first, emptied
 * the box again and pressed Save. Left out, that was "nothing changed" — the window
 * shut, and the colleague's reference stayed over a box the reader had just been
 * promised would be put in its place.
 */
export function changesOf(
  fields: readonly FieldSpec[],
  values: FormValues,
  opened: Readonly<Record<string, unknown>>,
  decided: ReadonlySet<string> = new Set(),
): { readonly changes: Record<string, unknown>; readonly was: Record<string, unknown> } {
  const now = wireValuesOf(fields, values, opened)
  const base = wireValuesOf(fields, formValuesOf(fields, opened), opened)
  const changes: Record<string, unknown> = {}
  const was: Record<string, unknown> = {}
  for (const field of fields) {
    const next = now[field.name]
    if (decided.has(field.name)) {
      const mine = next === undefined ? clearedOf(field) : { value: next }
      if (mine !== null) changes[field.name] = mine.value
      continue
    }
    if (next === undefined) continue
    if (JSON.stringify(next) === JSON.stringify(base[field.name])) continue
    changes[field.name] = next
    const from = wasOf(field, opened[field.name])
    if (from !== null) was[field.name] = from.value
  }
  return { changes, was }
}

/**
 * What a stored value is on the wire, as the thing a change is made **from** — or
 * null when there is no way to say it.
 *
 * `wireValueOf` after `asFormValue`, with two exceptions that are the point of having
 * this at all. A field that held nothing is said as its schema says nothing
 * (`clearedOf`): `null` for a due date a task did not have. And an instant is handed
 * back **as it was stored** rather than through the box — a `datetime-local` holds
 * minutes, and a due date an agent set to the second would otherwise be "changed
 * from" a time the task never had, and refused for it every time.
 */
export function wasOf(field: FieldSpec, stored: unknown): { readonly value: unknown } | null {
  if (isBlank(stored)) return clearedOf(field)
  if (field.widget === 'datetime' && typeof stored === 'string') return { value: stored }
  return { value: wireValueOf(field, asFormValue(field, stored)) }
}

/* ------------------------------------------------------------- taking away */

/**
 * What a field is on the wire once its value has been taken away — or `null`, when
 * the published schema has no way to say so. Wrapped, because `null` is one of the
 * three things a field can be cleared *to* and must not also be the answer "it cannot".
 */
export type Cleared = { readonly value: null | '' | readonly never[] }

/**
 * How one field is emptied, read off its schema and nothing else.
 *
 * `tasks_update` is the case that found it. The reader empties the Due box and
 * presses Save; the body was `{ task_ref, idempotency_key }`, the API answered 200,
 * the dialog closed and the date was still there — under a hint that says "Leave it
 * blank and it has no date". Three answers, in the order a schema is asked:
 *
 *   1. **`null`, where the schema takes it.** `due_at`, `service_code`, `money_ref`
 *      and `money` are `.nullable()` for exactly this, and each one's published
 *      description says "or null to take it off".
 *   2. **An empty list, or an empty string, where the schema sets no floor.** A list
 *      with no `minItems` is cleared by `[]`, and a box of free text with no
 *      `minLength` by `''` — `instructions` and `acceptance` are plain strings and
 *      that is the only way to empty them. **Free text only**: a picker, a day and a
 *      reference each *name* something, and `''` names nothing — it would be a
 *      service code, a date or a slug that is not one.
 *   3. **Nothing.** A title has `minLength: 3`; a number has no empty value. Such a
 *      field cannot be emptied, and `cannotBeEmptied` is how a form says so instead
 *      of dropping it.
 *
 * It is a rule about the **shape**, so it holds for every action without naming one:
 * `work_orders_update` clears a scope and refuses to clear a title by the same three
 * lines.
 */
export function clearedOf(field: FieldSpec): Cleared | null {
  if (field.nullable === true) return { value: null }
  if (LISTS.has(field.widget)) return field.minItems === undefined ? { value: [] } : null
  if (FREE_TEXT.has(field.widget)) return field.minLength === undefined ? { value: '' } : null
  return null
}

/** The widgets whose wire value is an array, so that "nothing" is an empty one. */
const LISTS: ReadonlySet<FieldWidget> = new Set(['tags', 'multiselect', 'rows'])

/** The widgets that hold words somebody wrote, rather than the name of something. */
const FREE_TEXT: ReadonlySet<FieldWidget> = new Set(['text', 'textarea'])

/**
 * True for a field the form must not let be submitted blank, **because it was opened
 * with a value and its schema has no way to take one away**.
 *
 * "Never silently drop it" is the rule this exists for. Leaving such a field out of
 * the body reads to a partial update as *unchanged*, so the reader would empty a
 * title, press Save, be told nothing and find the title still there. A form that
 * draws a field this is true of draws it as required instead — `ActionForm` does it
 * with the switch its `required` prop already had — so the refusal is the field's own
 * sentence, under the field's own box, before anything is sent.
 *
 * **A checkbox is never one.** It has no empty state: unticked is `false`, which is
 * an answer and is sent as one, so there is nothing to refuse and its label keeps
 * what it said.
 */
export function cannotBeEmptied(
  field: FieldSpec,
  opened: Readonly<Record<string, unknown>> | undefined,
): boolean {
  if (field.widget === 'checkbox') return false
  return wasFilled(field, opened) && clearedOf(field) === null
}

/** True for a field the form was opened with a value in. No `opened`, no such field. */
function wasFilled(
  field: FieldSpec,
  opened: Readonly<Record<string, unknown>> | undefined,
): boolean {
  return opened !== undefined && !isBlank(opened[field.name])
}

/** True for a field nobody filled in. `false` and `0` are answers, not blanks. */
export function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (isMoneyValue(value)) return value.major.trim() === ''
  return false
}

/** One field's value, as the type the API takes for it. */
export function wireValueOf(field: FieldSpec, value: unknown): unknown {
  if (field.widget === 'money' && isMoneyValue(value)) return amountOf(value.major, value.currency)
  if (field.widget === 'number' || field.widget === 'qty') {
    return typeof value === 'string' ? Number(value) : value
  }
  // A `<select>`'s value is a string even over `{ type: 'number', enum: [1, 2, 3] }`,
  // so a numeric list is converted back on the way out (`schemaFields.ts` `numeric`).
  if (field.widget === 'select' && field.numeric === true && typeof value === 'string') {
    return Number(value)
  }
  if (field.widget === 'checkbox') return value === true
  /*
   * `2026-09-04T17:00` → `2026-09-04T14:00:00.000Z`.
   *
   * A `datetime-local` box holds a wall time with no zone, and the reader's zone is
   * the one they mean — `new Date(local)` is what the platform reads it as, and
   * `toISOString` is the RFC 3339 the API takes. This is not a clock read: nothing
   * here asks what time it is now, which is why it is not `integrations/clock.ts`'s.
   */
  if (field.widget === 'datetime' && typeof value === 'string') return instantOf(value)
  if (field.widget === 'scalar' && typeof value === 'string') return scalarOf(value)
  if (field.widget === 'tags' && typeof value === 'string') return linesOf(value)
  /*
   * A row is a small form of the widgets above, so it is this function's own loop —
   * and it is `wireValuesOf` rather than `ActionForm`'s `bodyOf`, which is right by
   * construction: `bodyOf` adds one action's assignee pair, `rowFieldsOf` builds a
   * row's fields from a shape alone, and a shape cannot produce an `assignee`.
   */
  if (field.widget === 'rows' && Array.isArray(value)) {
    return value.map((row) => wireValuesOf(field.fields ?? [], row as FormValues))
  }
  return value
}

/**
 * `"645"` → `$645.00`; anything that is not a number → zero.
 *
 * `fromMajorUnits` is the only conversion (`CLAUDE.md` §Gotchas: `8.29 * 100` is
 * `828.9999999999999`), and a half-typed box is zero rather than `NaN` — which would
 * make a live remainder line say `$NaN` on every keystroke that starts with a minus.
 *
 * It lived in `components/studio/ReviewQueueItem.tsx` and was imported from there by
 * `ActionForm` and `InvoiceMoves`, which is one file too deep for what it is: it is
 * the `money` **widget's** own conversion, and the widget is this folder's.
 */
export function amountOf(major: string, currency: Money['currency']): Money {
  const trimmed = major.trim()
  if (trimmed === '' || Number.isNaN(Number(trimmed))) return zero(currency)
  return fromMajorUnits(trimmed, currency)
}

/** One per line, blanks dropped. What a `tags` textarea means. */
function linesOf(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

/**
 * `"true"` → `true`, `"14"` → `14`, `"America/New_York"` → itself.
 *
 * Only `settings_set`'s `value` reaches this. The order matters: no key in
 * `SETTING_KEYS` takes the literal string `"true"`, and several take a number
 * (`billing.rate_floor`, `notifications.daily_recipient_cap`).
 */
function scalarOf(raw: string): boolean | number | string {
  const value = raw.trim()
  if (value === 'true') return true
  if (value === 'false') return false
  if (value !== '' && !Number.isNaN(Number(value))) return Number(value)
  return raw
}
